<script>
    import CardCalendar from './CardCalendar.vue';
    import UserResource from '@/resources/user'

    export default {
        name: 'BoardCalendar',
        components: {
            CardCalendar
        },
        data () {
            return {
                isDragging: false,
                startX: 0,
                scrollLeft: 0,
                calendar: [],
                isLoading: true,
                selectedDate: new Date()
            }
        },
        computed: {
            isToday () {
                const today = new Date()
                return this.selectedDate.toDateString() === today.toDateString()
            },
            formattedDate () {
                if (this.isToday) return "Aujourd'hui"
                return this.$moment(this.selectedDate).format('dddd D MMMM YYYY')
            },
            dateParam () {
                return this.$moment(this.selectedDate).format('YYYY-MM-DD')
            }
        },
        created () {
            this.loadEvent()
        },
        methods: {
            parseDate (date) {
                date = date.split(' ')
                let d = date[0].split('/')
                d = d.concat(date[1].split(':'))
                return new Date(d[2], d[1] - 1, d[0], d[3], d[4])
            },
            changeDate (offset) {
                const newDate = new Date(this.selectedDate)
                newDate.setDate(newDate.getDate() + offset)
                this.selectedDate = newDate
                this.loadEvent()
            },
            goToToday () {
                this.selectedDate = new Date()
                this.loadEvent()
            },
            loadEvent () {
                this.calendar = []
                this.isLoading = true
                const dateStr = this.isToday ? null : this.dateParam
                UserResource.fetchCalendar(dateStr).then((result) => {
                    if (result.data.success) {
                        const parsedData = result.data.rdvs.map((rdv) => ({
                            date: this.parseDate(rdv.start_date),
                            hour: {
                                start: this.parseDate(rdv.start_date),
                                end: this.parseDate(rdv.end_date)
                            },
                            type: rdv.type,
                            customer: rdv.client.user.first_name + ' ' + rdv.client.user.last_name,
                            clientId: rdv.client.user.id,
                            clientPhone: rdv.client.user.phone || null,
                            clientEmail: rdv.client.user.email || null,
                            id: rdv.id,
                            description: rdv.description || null
                        }))
                        this.calendar = parsedData
                    }
                }).catch((error) => {
                    console.error('Erreur fetchAppointments:', error)
                }).finally(() => {
                    this.isLoading = false
                })
            },
            handleMouseDown (e) {
                this.isDragging = true
                const slider = this.$refs.eventsContainer
                this.startX = e.pageX - slider.offsetLeft
                this.scrollLeft = slider.scrollLeft
            },
            handleMouseLeave () {
                this.isDragging = false
            },
            handleMouseUp () {
                this.isDragging = false
            },
            handleMouseMove (e) {
                if (!this.isDragging) return
                e.preventDefault()
                const slider = this.$refs.eventsContainer
                const x = e.pageX - slider.offsetLeft
                const walk = (x - this.startX) * 2
                slider.scrollLeft = this.scrollLeft - walk
            }
        }
    }
</script>

<template lang="html">
	<section class="board-calendar dashboard-section--card">
		<div class="board-calendar__header">
			<h1 class="dashboard-title">Agenda</h1>
			<div class="board-calendar__nav">
				<button class="board-calendar__nav-btn" @click="changeDate(-1)" title="Jour précédent">
					<svg width="16" height="16" viewBox="0 0 20 20" fill="none">
						<path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</button>
				<button
					class="board-calendar__date-label"
					:class="{ 'board-calendar__date-label--today': isToday }"
					@click="goToToday"
					:title="isToday ? '' : 'Revenir à aujourd\'hui'"
				>
					{{ formattedDate }}
				</button>
				<button class="board-calendar__nav-btn" @click="changeDate(1)" title="Jour suivant">
					<svg width="16" height="16" viewBox="0 0 20 20" fill="none">
						<path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</button>
			</div>
		</div>
		<div
			class="board-calendar__events dashboard-scroll dashboard-scroll--draggable"
			ref="eventsContainer"
			@mousedown="handleMouseDown"
			@mouseleave="handleMouseLeave"
			@mouseup="handleMouseUp"
			@mousemove="handleMouseMove"
		>
			<div v-if="isLoading" class="board-calendar__loader">
				<span class="board-calendar__spinner"></span>
			</div>
			<template v-else-if="calendar.length > 0">
				<div class="board-calendar__event" v-for="event in calendar" :key="event.id">
					<CardCalendar :item="event" />
				</div>
			</template>
			<div v-else class="board-calendar__empty">
				<p>Aucun rendez-vous {{ isToday ? "aujourd'hui" : 'ce jour' }}</p>
			</div>
		</div>
	</section>
</template>

<style scoped>
.board-calendar {
	margin: 1rem 0;
}

.board-calendar__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 0.5rem;
}

.board-calendar__nav {
	display: flex;
	align-items: center;
	gap: 0.25rem;
}

.board-calendar__nav-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	background: #fff;
	color: #495057;
	cursor: pointer;
	transition: all 0.2s ease;
}

.board-calendar__nav-btn:hover {
	background: #f5f5f5;
	border-color: #AF9376;
	color: #AF9376;
}

.board-calendar__date-label {
	padding: 0.35rem 0.75rem;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	background: #fff;
	font: 600 0.875rem "Lato", sans-serif;
	color: #495057;
	cursor: pointer;
	transition: all 0.2s ease;
	text-transform: capitalize;
}

.board-calendar__date-label:hover {
	border-color: #AF9376;
	color: #AF9376;
}

.board-calendar__date-label--today {
	background: #AF9376;
	border-color: #AF9376;
	color: #fff;
	cursor: default;
}

.board-calendar__date-label--today:hover {
	background: #AF9376;
	border-color: #AF9376;
	color: #fff;
}

.board-calendar__events {
	display: flex;
	gap: 1rem;
	overflow-x: auto;
	margin-top: 1rem;
}

.board-calendar__event {
	flex: 0 0 calc(33.333% - 0.67rem);
	min-width: 280px;
}

.board-calendar__empty {
	width: 100%;
	text-align: center;
	padding: 1rem 0;
	color: #979797;
}

.board-calendar__loader {
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 2rem 0;
    margin:auto;
}

.board-calendar__spinner {
	display: inline-block;
	width: 28px;
	height: 28px;
	border: 3px solid rgba(175, 147, 118, 0.2);
	border-top-color: #AF9376;
	border-radius: 50%;
	animation: board-calendar-spin 0.7s linear infinite;
}

@keyframes board-calendar-spin {
	to { transform: rotate(360deg); }
}

@media (max-width: 1024px) {
	.board-calendar__event {
		flex: 0 0 calc(50% - 0.5rem);
		min-width: 250px;
	}
}

@media (max-width: 768px) {
	.board-calendar__event {
		flex: 0 0 100%;
		min-width: 100%;
	}
}
</style>



// WEBPACK FOOTER //
// BoardCalendar.vue?cc60e418